/**
 * Resolving the parent plugin a derived skill came from (issue #172).
 *
 * A "derived skill" is a synthesized catalog entry the processor produces from a
 * plugin's embedded skills[] (catalog-builder.ts synthesizeSkillEntries). The child
 * carries no pointer back to its parent, but each parent plugin's skills[].installName
 * is stamped with that child skill's own marketplace installName
 * (catalog-builder.ts enrichSkillsWithInstallNames). These helpers invert that link.
 */

import type { MarketplaceEntry } from "../types/index";

/**
 * Build a reverse index: a derived skill's installName → its parent plugin entry.
 *
 * Only same-repo links are kept. A synthesized skill always lives in its parent
 * plugin's repo (synthesizeSkillEntries copies source/orgOrOwner/repoName). The
 * catalog builder's name-keyed back-fill can otherwise point a plugin's embedded
 * skill at an *unrelated standalone entry* that merely shares its name — possibly
 * in another repo — because standalone records override synthesized ones in the
 * name index. The repo check rejects those, so an unrelated entry's detail page
 * never gets labelled with a bogus parent.
 */
export function buildParentIndex(catalog: MarketplaceEntry[]): Map<string, MarketplaceEntry> {
  const entryByInstallName = new Map<string, MarketplaceEntry>();
  for (const entry of catalog) {
    if (entry.installName) entryByInstallName.set(entry.installName, entry);
  }

  const map = new Map<string, MarketplaceEntry>();
  for (const parent of catalog) {
    if (parent.extensionType !== "plugin" || !parent.skills?.length) continue;
    for (const skill of parent.skills) {
      const childInstallName = skill.installName;
      // Skip missing links and any link pointing back at this same plugin
      // (a skill sharing its plugin's name; the builder normally already drops it).
      if (!childInstallName || childInstallName === parent.installName) continue;
      const child = entryByInstallName.get(childInstallName);
      // Require the resolved child to share the parent's repo (see doc comment).
      if (
        !child ||
        child.source !== parent.source ||
        child.orgOrOwner !== parent.orgOrOwner ||
        child.repoName !== parent.repoName
      ) continue;
      // First writer wins on the rare duplicate child installName.
      if (!map.has(childInstallName)) map.set(childInstallName, parent);
    }
  }
  return map;
}

/**
 * Breadcrumb trail for a detail page. Prefer the nav-state chain — a plugin→skill
 * click builds full ancestry there. On a direct visit or deep link that chain is
 * empty, so fall back to the data-resolved parent → Marketplace › Plugin › Skill.
 */
export function computeEffectiveBreadcrumbs(
  breadcrumbs: MarketplaceEntry[],
  parentPlugin: MarketplaceEntry | undefined,
): MarketplaceEntry[] {
  if (breadcrumbs.length > 0) return breadcrumbs;
  return parentPlugin ? [parentPlugin] : [];
}
